"use client";
import React, { useContext, useEffect, useMemo, useState } from "react";
import Script from "next/script";

import useUserIdentification from "@stadium/services/analytics/hooks/use-user-identification";

import { getIsUserBlacklisted } from "@stadium/services/analytics/helpers/blacklisted-users";
import { mixpanelScriptSrc } from "@stadium/services/mixpanel-analytics/constants";
import { MIXPANEL } from "@stadium/services/analytics/constants/services";

const defaultObj = {};

export const MixpanelContext = React.createContext(defaultObj);

export const useMixpanelContext = () => useContext(MixpanelContext);

const defaultCurrentUserData = {
  email: "",
  firstName: "",
  lastName: "",
  spreeId: "",
  company: defaultObj,
  profile: defaultObj,
};

const getDefaultConfig = (config) => ({
  autocapture: false,
  ...config,
});

export const MixpanelProvider = ({ children, apiKey, config = defaultObj }) => {
  const [isMixpanelScriptLoaded, setIsMixpanelScriptLoaded] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(defaultCurrentUserData);
  const [isUserDataLoaded, setIsUserDataLoaded] = useState(false);
  const [pageName, setPageName] = useState("");

  const services = [MIXPANEL];
  const { identifyUser, groupUser } = useUserIdentification(services);

  // Minimal stub: queue init for CDN script to process when it loads (matches official snippet pattern)
  // Only treat as loaded when the real SDK is present (has identify/track). Our stub has __SV but lacks
  // these methods—checking __SV would falsely mark loaded on effect re-runs when the stub exists.
  useEffect(() => {
    if (!apiKey || globalThis.document === undefined) {
      return;
    }
    if (typeof globalThis.mixpanel?.identify === "function") {
      setIsMixpanelScriptLoaded(true);
      return;
    }
    if (!globalThis.mixpanel) {
      globalThis.mixpanel = [];
      globalThis.mixpanel._i = [];
      globalThis.mixpanel.init = function (token, opts, name) {
        globalThis.mixpanel._i.push([token, opts, name || "mixpanel"]);
      };
      globalThis.mixpanel.__SV = 1.2;
    }
    globalThis.mixpanel.init(apiKey, getDefaultConfig(config));
  }, [apiKey, config]);

  useEffect(() => {
    if (isMixpanelScriptLoaded && isUserDataLoaded) {
      const { email, firstName, lastName, name, spreeId: userId, company } = currentUserData;
      const displayName = name || [firstName, lastName].filter(Boolean).join(" ").trim();
      identifyUser({
        userId,
        userProperties: { email, name: displayName },
        additionalProps: { companyName: company?.name },
      });
      groupUser({ companyId: company?.id, companyProperties: { name: company?.name } });
    }
  }, [isMixpanelScriptLoaded, isUserDataLoaded, currentUserData.email]);

  const onLoad = () => {
    setIsMixpanelScriptLoaded(true);
  };

  const value = useMemo(
    () => ({
      setCurrentUserData,
      setIsUserDataLoaded,
      isUserDataLoaded,
      isMixpanelScriptLoaded,
      isMixpanelSetupCompleted: isMixpanelScriptLoaded && isUserDataLoaded,
      setPageName,
      pageName,
      currentUserData,
      isLoggedInUserBlackListed: getIsUserBlacklisted({
        companyName: currentUserData?.company?.name,
      }),
    }),
    [isUserDataLoaded, isMixpanelScriptLoaded, pageName, currentUserData],
  );

  const scriptSrc =
    globalThis.document !== undefined && globalThis.MIXPANEL_CUSTOM_LIB_URL
      ? globalThis.MIXPANEL_CUSTOM_LIB_URL
      : mixpanelScriptSrc;

  return (
    <>
      {apiKey && (
        <Script
          type="application/javascript"
          onLoad={onLoad}
          src={scriptSrc}
          strategy="lazyOnload"
        />
      )}
      <MixpanelContext.Provider value={value}>{children}</MixpanelContext.Provider>
    </>
  );
};
